import { between } from '@iotv/core-services'
import { dayMaskAllTrueTemplate, dayMaskTemplate, isNonNaNNumber, Milliseconds, type ValidBusinessObject } from '@iotv/iotv-v3-types'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { v4 } from 'uuid'
import { UserAttributeKeyDatePrecision, type UserAttributeDefs } from '../../../types/ModelTypes'
import { getReasonableValueNumber } from '../../../util/time/helpers'
import { ModelCommon } from '../../ModelCommon'
import { OnDemandProgrammeConstructorProps, supportedTypes, type OnDemandProgrammeDef } from './types'

dayjs.extend(utc)

const reasonableWindowcount = getReasonableValueNumber(1, 1000)

export const isOnDemandProgramme = (x: any): x is OnDemandProgrammeConstructorProps => {
  const c = x as OnDemandProgrammeConstructorProps
  const isConstructorProps = isOnDemandProgrammeConstructorProps(x)
  const isInstance = c instanceof OnDemandProgramme
  const isOnDemandProgramme = isConstructorProps && isInstance
  if (c && !isOnDemandProgramme) {
    ;(c as OnDemandProgrammeConstructorProps & ValidBusinessObject)?.type === 'OnDemandProgramme' &&
      console.log(`WARN isOnDemandProgramme returning false for OnDemandProgramme ${c.id} with`, { isConstructorProps, isInstance })
  }
  return isOnDemandProgramme
}

export const isOnDemandProgrammeConstructorProps = (x: any): x is OnDemandProgrammeConstructorProps => {
  const { name, supportedType, startTime, windows, windowDuration, windowGapDuration, repeats } = (x as OnDemandProgrammeConstructorProps) ?? {}
  const isOnDemandProgrammeConstructorProps =
    typeof name === 'string' &&
    typeof supportedType === 'string' &&
    (startTime === null || isNonNaNNumber(startTime)) &&
    (windows === null || isNonNaNNumber(windows)) &&
    (windowDuration === null || isNonNaNNumber(windowDuration)) &&
    (windowGapDuration === null || isNonNaNNumber(windowGapDuration)) &&
    (repeats === null || isNonNaNNumber(repeats))
  if (!isOnDemandProgrammeConstructorProps) {
    ;(x as OnDemandProgrammeConstructorProps & ValidBusinessObject)?.type === 'OnDemandProgramme' &&
      console.log(`WARN isOnDemandProgrammeConstructorProps returning false for OnDemandProgramme ${x?.id} with`, {
        name,
        supportedType,
        startTime,
        windows,
        windowDuration,
        windowGapDuration,
        repeats,
      })
  }

  return isOnDemandProgrammeConstructorProps
}

export class OnDemandProgramme extends ModelCommon {
  constructor({
    id = v4(),
    supportedType,
    name,
    appliedTimestamps,
    startTime,
    windows,
    windowDuration,
    windowGapDuration,
    repeats,
    ...others
  }: OnDemandProgrammeConstructorProps) {
    super('OnDemandProgramme', { id, ...others })
    this.name = name
    this.supportedType = supportedType
    this.appliedTimestamps = appliedTimestamps ?? null
    this.startTime = startTime ?? 0
    this.windows = reasonableWindowcount(windows) ? (windows ?? 1) : 1
    this.windowDuration = windowDuration ?? 1
    this.windowGapDuration = windowGapDuration ?? 1
    this.repeats = repeats ?? 1
  }

  supportedType: string
  name: string
  appliedTimestamps: null | number[]
  startTime: number
  windows: number
  windowDuration: number
  windowGapDuration: number
  repeats: number

  getUserAttributes(): UserAttributeDefs {
    return {
      ...super.getUserAttributes(),
      supportedType: {
        type: 'Enum',
        editable: true,
        required: true,
        enumKv: supportedTypes,
      },
      startTime: {
        type: 'Date',
        editable: false,
        required: true,
        precision: UserAttributeKeyDatePrecision.DateTimeMins,
        label: 'Start Time',
        displayFn: (v: number) =>
          dayjs
            .utc(v * Milliseconds.SECOND)
            .local()
            .valueOf(),
        valueFn: (v: number) => v / Milliseconds.SECOND,
      },
      windows: {
        type: 'Number',
        editable: false,
        required: true,
        precision: 0,
        label: 'Windows',
        default: 1,
        limits: { min: 1 },
      },
      windowDuration: {
        type: 'Number',
        editable: false,
        required: true,
        precision: 0,
        label: 'Duration',
        units: 'minutes',
        default: 1,
        limits: { min: 1 },
      },
      windowGapDuration: {
        type: 'Number',
        editable: false,
        required: true,
        precision: 0,
        label: 'Window Gap',
        units: 'minutes',
        default: 1,
      },
      repeats: {
        type: 'Number',
        editable: false,
        required: true,
        precision: 0,
        label: 'Repeats',
        limits: { min: 1, max: 5 },
      },
    }
  }

  updateAppliedTimestamps = (timestampMs: number) => {
    if (!this.appliedTimestamps) this.appliedTimestamps = []
    this.appliedTimestamps.unshift(timestampMs)
    this.appliedTimestamps = this.appliedTimestamps.slice(0, this.maxAppliedTimestamps)
  }

  /**
   * The total cycle time in minutes
   *
   * Includes:
   * - number of windows
   * - window duration
   * - window gap duration
   * - repeats
   *
   * Exclude the last gap duration, as there is nothing following it
   */
  getTotalRepeatTime = () => this.windows * (this.windowDuration + this.windowGapDuration) * this.repeats - this.windowGapDuration

  /**
   * The calculated `delayMins` to apply to each `rollingProgammeConfig`
   *
   * Based on:
   * - number of windows
   * - window duration
   * - window gap duration
   *
   * The device enters the delay phase first, then the operation phase (`windowDuration`), so every config (including the first) starts with the calculated delay
   */
  getCalculatedDelayMins = () => this.getTotalRepeatTime() - this.windowDuration

  /**
   * Calculates a config for any window index
   */
  getOnDemandProgrammeConfigForWindow = (window: number, realtime: boolean = false): OnDemandProgrammeDef | null => {
    let res: OnDemandProgrammeDef | null = null
    const { startTime, windows, windowDuration, windowGapDuration, repeats } = this
    if (between(window, 0, windows * repeats)) {
      const now = dayjs().unix()
      const duration = this.getTotalRepeatTime()

      const disabled = startTime === 0
      const shouldDisable = startTime * Milliseconds.SECOND + duration * Milliseconds.MINUTE <= now
      const disable = disabled || shouldDisable

      const interCycleTimeMins = windowDuration + windowGapDuration
      /** offset from '0' time, in minutes */
      const offset = interCycleTimeMins * window

      const start = dayjs(realtime ? startTime * Milliseconds.SECOND : 0)
        .utc()
        .add(offset, 'minutes')

      const startHour = start.hour()
      const startMinute = start.minute()

      res = {
        enable: !disable,
        dayMask: disable ? dayMaskTemplate : dayMaskAllTrueTemplate,
        scheduleNumber: 1 + Math.floor(window / windows),
        moistureThreshold: 0,
        duration_mins: windowDuration,
        utc_start_hour: startHour,
        utc_start_min: startMinute,
      }
    } else {
      console.log(`WARN getOnDemandProgrammeConfigForWindow ${window} is out of bounds, max is ${windows * repeats - 1}`)
    }

    return res
  }
}
